<script lang="ts">
import type { MapboxRetrieved, MapboxSearchType } from "../composables/useAppMapboxSearch";

export interface InputLocationExtraItem {
  value: string;
  label: string;
  description?: string;
  icon?: string;
  trailingLabel?: string;
  onSelect: () => void | Promise<void>;
}

export interface InputLocationExtraGroup {
  label?: string;
  items: InputLocationExtraItem[];
}

export type { MapboxRetrieved };
</script>

<script setup lang="ts">
import { formBusInjectionKey, formFieldInjectionKey } from "#ui/composables/useFormField";
import { parseCoordinateSuggestions } from "../utils/coordinates";
import type { CoordinateFormatSlug } from "../utils/coordinates";

// Combobox de recherche de lieu (Mapbox) partagé web/mobile. Consentement Mapbox INJECTÉ
// (`mapboxAllowed`) : enveloppe web → cookie-consent, mobile → toujours autorisé. Toute la
// mécanique Reka (synchro searchTerm / model / affichage, commit synchrone à la sélection)
// est conservée à l'identique — voir les commentaires détaillés.
type Size = "md" | "lg" | "xl";

type DisplayItem = InputLocationExtraItem;
type GroupItem = DisplayItem & { type: "label" };

function groupLabel(label: string): GroupItem {
  return { type: "label", value: "", label, onSelect: () => {} };
}

const props = withDefaults(
  defineProps<{
    placeholder?: string;
    size?: Size;
    types?: MapboxSearchType[];
    language?: string;
    limit?: number;
    extraGroups?: InputLocationExtraGroup[];
    extraLoading?: boolean;
    mapboxGroupLabel?: string;
    mapboxAllowed?: boolean;
    withCoordinates?: boolean;
    boostBroadFeatures?: boolean;
  }>(),
  {
    size: "md",
    language: "fr",
    limit: 4,
    extraGroups: () => [],
    extraLoading: false,
    placeholder: undefined,
    types: undefined,
    mapboxGroupLabel: undefined,
    mapboxAllowed: true,
    withCoordinates: false,
    boostBroadFeatures: false,
  }
);

const emit = defineEmits<{
  select: [MapboxRetrieved];
  search: [term: string];
}>();

const model = defineModel<string>({ default: "" });

const { t } = useI18n();

const formBus = inject(formBusInjectionKey, undefined);
const formField = inject(formFieldInjectionKey, undefined);

function toDisplayItem(name: string): DisplayItem {
  return { value: name, label: name, onSelect: () => {} };
}

// model-value vu par Reka, découplé du `model` du formulaire : vider `model` à
// l'édition relancerait le resetSearchTerm() interne de Reka et effacerait la saisie.
const displayItem = ref<DisplayItem | undefined>(model.value ? toDisplayItem(model.value) : undefined);
const searchTerm = ref("");

let clearingFromEdit = false; // effacement dû à l'édition (≠ reset externe), cf. watch(model)
let syncingSearchTerm = false; // resync de searchTerm après sélection, cf. onSelect

watch(model, (v) => {
  const name = formField?.value?.name;
  if (formBus && name) formBus.emit({ type: "change", name });
  if (v) {
    if (v !== displayItem.value?.value) displayItem.value = toDisplayItem(v);
  } else if (!clearingFromEdit) {
    // model vidé de l'extérieur (pas par l'édition) → on efface affichage + texte.
    displayItem.value = undefined;
    searchTerm.value = "";
  }
  clearingFromEdit = false;
});

const inputMenuRef = useTemplateRef<{ inputRef: HTMLInputElement }>("inputMenu");

watchEffect(() => {
  const el = inputMenuRef.value?.inputRef;
  if (!el) return;
  el.name = "location";
  el.autocomplete = "off";
});

const mapboxSearch = useAppMapboxSearch({
  language: () => props.language,
  limit: () => props.limit,
  types: () => props.types,
  mapboxAllowed: () => props.mapboxAllowed,
  boostBroadFeatures: () => props.boostBroadFeatures,
});

function resetInput() {
  searchTerm.value = "";
  mapboxSearch.clear();
  committedItems.value = [];
}

// Commit SYNCHRONE de la valeur : à la sélection, NuxtUI émet form-blur (fermeture
// du dropdown) puis form-change AVANT toute résolution asynchrone (retrieve Mapbox) — un
// `model` encore vide à cet instant flashe le champ requis en erreur. Reka appelle
// item.onSelect tout au début du select (avant la fermeture), donc poser la valeur ici suffit.
// syncingSearchTerm : NuxtUI remet searchTerm à "" (resetSearchTermOnSelect), que
// watch(searchTerm) prendrait pour une édition (et reviderait `model`). On réaligne
// searchTerm sur le label au tick suivant (committedItems déjà vidé → dropdown reste
// fermé), puis on rouvre watch — fenêtre courte, sans attendre le réseau.
async function commitSelection(name: string) {
  syncingSearchTerm = true;
  displayItem.value = toDisplayItem(name);
  model.value = name;
  mapboxSearch.clear();
  committedItems.value = [];
  await nextTick();
  searchTerm.value = name;
  await nextTick();
  syncingSearchTerm = false;
}

const COORDINATE_FORMAT_LABEL_KEYS: Record<CoordinateFormatSlug, string> = {
  dd: "location.search.format.dd",
  dm: "location.search.format.dm",
  dms: "location.search.format.dms",
  olc: "location.search.format.olc",
};

const NO_COORDINATE_ITEMS: DisplayItem[] = [];
const coordinateItems = computed<DisplayItem[]>(() => {
  if (!props.withCoordinates) return NO_COORDINATE_ITEMS;
  const suggestions = parseCoordinateSuggestions(searchTerm.value.trim());
  if (suggestions.length === 0) return NO_COORDINATE_ITEMS;
  return suggestions.map((suggestion) => {
    const formatLabel = t(COORDINATE_FORMAT_LABEL_KEYS[suggestion.formatSlug]);
    return {
      value: `coordinates:${suggestion.formatSlug}:${suggestion.label}`,
      label: suggestion.label,
      description: formatLabel,
      icon: "icon:crosshair",
      onSelect: async () => {
        await commitSelection(suggestion.label);
        const [latitude, longitude] = suggestion.latLon;
        emit("select", { center: [longitude, latitude], name: suggestion.label, placeFormatted: formatLabel });
      },
    };
  });
});

function buildItems(): DisplayItem[] {
  const items: DisplayItem[] = [...coordinateItems.value];

  if (mapboxSearch.suggestions.value.length > 0) {
    if (props.mapboxGroupLabel) items.push(groupLabel(props.mapboxGroupLabel));
    for (const s of mapboxSearch.suggestions.value) {
      items.push({
        value: s.mapbox_id,
        label: s.name,
        description: s.place_formatted ?? [s.context?.region?.name, s.context?.country?.name].filter(Boolean).join(", "),
        icon: "icon:crosshair",
        onSelect: async () => {
          // Adresse : le full_address de la suggestion sert d'affichage immédiat (le `name` seul
          // n'est que la voie). Commit synchrone AVANT retrieve pour éviter le flash requis.
          const initialName = s.feature_type === "address" && s.full_address ? s.full_address : s.name;
          await commitSelection(initialName);
          // Coordonnées ET nom définitif en arrière-plan : retrieved.name est la source de vérité
          // (formaté « adresse, code postal ville, pays ») partagée par le champ et la chip de filtre.
          const retrieved = await mapboxSearch.retrieve(s);
          if (retrieved) {
            if (retrieved.name !== initialName) await commitSelection(retrieved.name);
            emit("select", retrieved);
          }
        },
      });
    }
  }

  for (const group of props.extraGroups) {
    if (group.items.length === 0) continue;
    if (group.label) items.push(groupLabel(group.label));
    items.push(...group.items);
  }

  return items;
}

const combinedLoading = computed(() => mapboxSearch.loading.value || props.extraLoading);
const committedItems = ref<DisplayItem[]>([]);

watch(searchTerm, (q) => {
  if (syncingSearchTerm) return;
  // Saisie dans un champ de formulaire → invalide la sélection (vide model) ; gardé au
  // cas formulaire pour ne pas écraser le filtre d'AppLocationSearch. clearingFromEdit
  // dit à watch(model) de conserver le texte saisi.
  if (formField?.value?.name && model.value) {
    clearingFromEdit = true;
    model.value = "";
  }
  if (q.trim().length < 2) committedItems.value = [];
  mapboxSearch.search(q);
  emit("search", q);
});

watch(combinedLoading, (isLoading) => {
  if (!isLoading && searchTerm.value.trim().length >= 2) committedItems.value = buildItems();
});

watch(coordinateItems, () => {
  if (syncingSearchTerm) return;
  if (searchTerm.value.trim().length >= 2) committedItems.value = buildItems();
});

const { open, dismiss } = useAppSearchEscape({
  searchTerm,
  canOpen: () => searchTerm.value.trim().length >= 2 && committedItems.value.length > 0,
});

function onOpenChange(value: boolean) {
  if (!value) dismiss();
}

function forceInputClear() {
  resetInput();
  // Reka peut maintenir un state DOM indépendant du v-model — on force la
  // valeur native de l'input + émet un event input pour resynchroniser.
  const el = inputMenuRef.value?.inputRef;
  if (el) {
    el.value = "";
    el.dispatchEvent(new Event("input", { bubbles: true }));
    el.focus();
  }
}

function clear() {
  model.value = "";
  displayItem.value = undefined;
  forceInputClear();
}

function clearSearch() {
  forceInputClear();
}

defineExpose({ inputRef: computed(() => inputMenuRef.value?.inputRef), clearSearch });
</script>

<template>
  <ClientOnly>
    <UInputMenu
      ref="inputMenu"
      v-model:search-term="searchTerm"
      :model-value="displayItem"
      :items="committedItems"
      :placeholder="placeholder"
      :size="size"
      color="primary"
      :ignore-filter="true"
      :reset-search-term-on-blur="false"
      :reset-search-term-on-select="true"
      :open="open"
      :ui="{ content: 'max-h-120 max-w-lg' }"
      :content="{ align: 'start' }"
      @update:open="onOpenChange"
    >
      <template #trailing="{ ui }">
        <AppSpinner v-if="combinedLoading" :class="[ui.trailingIcon(), 'size-5 md:size-6', { 'p-0.5': ['lg', 'xl'].includes(size) }]" />
        <UButton
          v-else-if="searchTerm || model"
          square
          variant="ghost"
          class="shrink-0 p-2 -mr-2"
          size="sm"
          :aria-label="t('location.search.clear')"
          @mousedown.prevent
          @click.stop="clear"
        >
          <UIcon name="icon:close" :class="ui.trailingIcon()" />
        </UButton>
        <UIcon v-else name="icon:search" :class="ui.trailingIcon()" />
      </template>
      <template #item="{ item }">
        <AppLocationSearchItem :label="item.label" :description="item.description" :icon="item.icon" :trailing-label="item.trailingLabel" />
      </template>
      <template #empty />
    </UInputMenu>
    <template #fallback>
      <UInput
        :model-value="model"
        :placeholder="placeholder"
        :size="size"
        color="primary"
        name="location"
        autocomplete="off"
        readonly
        trailing-icon="icon:search"
      />
    </template>
  </ClientOnly>
</template>
